import { firstValueFrom } from 'rxjs';
import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges } from '@angular/core';
import { CustomComponent, ngHelper } from '@cakeiteasy/web-utils';
import { ICart } from '../../../cart';
import { PaymentApiService } from '../../../api-services/services/payment-api.service';
import { IWebhookUrls } from '../../models/i-payment-models';
import { KlarnaCheckoutService } from '../../services/klarna-checkout.service';
import { CART_PROMO_CODE_TYPES } from '../../../cart-ui/constants/common';

@Component({
  standalone: false,
  selector: 'klarna-payment',
  templateUrl: './klarna-payment.component.html',
  styleUrls: ['./klarna-payment.component.scss'],
})
export class KlarnaPaymentComponent extends CustomComponent implements OnChanges {
  @Input() public webhookUrls: IWebhookUrls;
  @Input() public isMobileLayout: boolean;
  @Input() public cart: ICart;
  @Input() public promoCardType: CART_PROMO_CODE_TYPES = null;
  @Input() public giftCardCode: string = null;
  @Input() public queueProcessing: Promise<any>;

  @Output() public onFail = new EventEmitter<any>();
  @Output() public onOrderCreate = new EventEmitter<string>();
  @Output() onBusy = new EventEmitter<Promise<any>>();

  public snippet: string;

  constructor(
    private _klarna: KlarnaCheckoutService,
    private _paymentApi: PaymentApiService,
  ) {
    super();
  }

  public ngOnChanges(changes: SimpleChanges) {
    ngHelper.ifChanged(changes, 'queueProcessing', async (queueProcessing) => {
      if (!this.cart || !this.cart.lines || this.cart.lines.length === 0) {
        return;
      }

      this.onBusy.emit((queueProcessing || Promise.resolve(null)).then(() => this._init()));
    });

    ngHelper.ifChanged(changes, 'promoCardType', (promoCardType: string) => {
      if (promoCardType) {
        this.onBusy.emit(this._init());
      }
    });

    ngHelper.ifChanged(changes, 'giftCardCode', (giftCardCode: string) => {
      if (giftCardCode) {
        this.onBusy.emit(this._init());
      }
    });
  }

  private _init() {
    return this._getKlarnaSnippet().then((snippet) => {
      this._putSnippet(snippet);
    });
  }

  private _putSnippet(snippet) {
    // This is necessary otherwise the scripts tags are not going to be evaluated
    const container = document.querySelector('[data-snippet]');
    this._klarna.insertSnippet(snippet, container);
  }

  private _getKlarnaSnippet(): Promise<string> {
    if (this.snippet) {
      return Promise.resolve(this.snippet);
    }

    const payload = {
      ...this.webhookUrls,
      layout: this.isMobileLayout ? 'mobile' : 'desktop',
      code: this.giftCardCode,
    };

    return firstValueFrom(this._paymentApi.createKlarnaPayment(payload))
      .then((data) => {
        this.onOrderCreate.emit(data.sid);
        return data.snippet;
      })
      .catch((err) => {
        this.onFail.emit(err);
        return null;
      });
  }
}
